import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { FC, PropsWithChildren } from 'react'
import { Colors, ColorUtil } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { WithClassName } from '~/types/utils'
import { OnClickProps } from '~/utils/onClickProps'

type TableRowProps = WithClassName & {
  clickable?: OnClickProps
  archived?: boolean
  className?: string
}

const Tr = styled.tr<{ isClickable: boolean; archived?: boolean }>`
  background: ${Colors.White};
  /* A floor, not a fixed height: a row of plain text settles on 48px, and one carrying an input or a
     wrapped cell is free to be taller rather than clipping what is in it.

     height, not min-height: min-height does not apply to a table row, so the declaration that used to sit
     here did nothing and rows were as tall as their content made them — 26px of line-height plus the
     cell's 4px top and bottom, i.e. 34px. On a table row, height is what CSS already treats as a minimum,
     which is the behaviour that comment was describing all along. */
  height: 48px;
  cursor: ${(props) => (props.isClickable ? 'pointer' : 'auto')};
  ${Fonts.colors.Jet};
  ${Fonts.P1};

  ${({ archived }) =>
    archived &&
    css`
      ${Fonts.colors.Silver};
    `}

  &:hover {
    background: linear-gradient(
      90deg,
      ${ColorUtil.hexOpacity(Colors.Platinum, 0.2)} 0%,
      ${ColorUtil.hexOpacity(Colors.Platinum, 0.3)} 20%,
      ${ColorUtil.hexOpacity(Colors.Platinum, 0.3)} 80%,
      ${ColorUtil.hexOpacity(Colors.Platinum, 0.2)} 100%
    );
  }
`

export const BaseTableRow: FC<PropsWithChildren<TableRowProps>> = ({ children, clickable, archived, className }) => (
  <Tr isClickable={!!clickable && !archived} onClick={(e) => clickable && clickable.onClick(e)} className={className} archived={archived} data-tracking={clickable?.trackingLabel}>
    {children}
  </Tr>
)

const TableRow = styled(BaseTableRow)``

/**
 * Clips every cell of a row rather than letting one long value stretch the column. Put on the rows of a
 * list whose values are names and ids, where the column widths matter more than seeing the whole value.
 */
export const ROW_CELL_CLASSES = '[&_td]:max-w-[350px] [&_td]:overflow-hidden [&_td]:text-ellipsis'

export default TableRow
